import {
  BORDER,
  DARK_MODE,
  GRAY_SCALE,
  THEME_COLOR,
  BODY_BACKGROUND,
} from 'constants/ThemeConstant';

export function rgba(hex, opacity = 1) {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    throw new Error('Invalid Hex');
  }

  const decimal = parseInt(hex.substring(1), 16);

  const r = (decimal >> 16) & 255;
  const g = (decimal >> 8) & 255;
  const b = decimal & 255;

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const baseTheme = {
  borderRadius: 10,
  controlHeight: 40,
  controlHeightLG: 48,
  // 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
  controlHeightSM: 36,
  fontSizeHeading2: 22,
  fontSizeHeading4: 17,
  bodyBg: BODY_BACKGROUND,
  colorText: GRAY_SCALE.GRAY,
  colorInfo: THEME_COLOR.BLUE,
  colorPrimary: THEME_COLOR.BLUE,
  colorSuccess: THEME_COLOR.CYAN,
  colorWarning: THEME_COLOR.GOLD,
  // fontFamily: `'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
  colorBorder: BORDER.BASE_COLOR,
  colorError: THEME_COLOR.VOLCANO,
  // 'Noto Color Emoji'`,
  fontFamily: `'Poppins', sans-serif`,
};

const getBaseComponentThemeConfig = ({
  Menu = {},
  Card = {},
  Table = {},
  Input = {},
  Button = {},
  Select = {},
  Layout = {},
  Dropdown = {},
  Calendar = {},
  DatePicker = {},
} = {}) => {
  return {
    Input: {
      ...Input,
    },
    Button: {
      ...Button,
    },
    Tag: {
      lineHeight: 2.1,
    },
    Calendar: {
      ...Calendar,
    },
    Radio: {
      fontSizeLG: 18,
    },
    Timeline: {
      lineType: 'dashed',
    },
    TreeSelect: {
      controlHeightSM: 24,
    },
    Checkbox: {
      lineWidth: 2,
      borderRadiusSM: 4,
    },
    Tooltip: {
      controlHeight: 32,
      colorBgDefault: rgba(GRAY_SCALE.DARK, 0.75),
    },
    Card: {
      paddingLG: 20,
      colorTextHeading: GRAY_SCALE.GRAY_DARK,
      ...Card,
    },
    Layout: {
      bodyBg: BODY_BACKGROUND,
      headerBg: GRAY_SCALE.WHITE,
      ...Layout,
    },
    Table: {
      colorText: GRAY_SCALE.GRAY,
      tableHeaderBg: 'transparent',
      ...Table,
    },
    Steps: {
      wireframe: true,
      controlHeight: 32,
      waitIconColor: GRAY_SCALE.GRAY_LIGHT,
    },
    Slider: {
      colorPrimaryBorderHover: baseTheme.colorPrimary,
      colorPrimaryBorder: rgba(baseTheme.colorPrimary, 0.8),
    },
    DatePicker: {
      lineWidthBold: 0,
      controlHeight: 40,
      borderRadiusSM: 6,
      controlHeightSM: 26,
      ...DatePicker,
    },
    Breadcrumb: {
      colorBgTextHover: 'transparent',
      colorText: baseTheme.colorPrimary,
      colorTextDescription: GRAY_SCALE.GRAY_LIGHT,
    },
    Dropdown: {
      controlHeight: 37,
      borderRadiusLG: 10,
      paddingXXS: '4px 0px',
      controlPaddingHorizontal: 20,
      ...Dropdown,
    },
    Select: {
      controlHeight: 40,
      controlHeightSM: 30,
      paddingXXS: '4px 0px',
      controlItemBgActive: rgba(baseTheme.colorPrimary, 0.1),
      ...Select,
    },
    Pagination: {
      borderRadius: '50%',
      paginationItemSize: 30,
      colorPrimary: GRAY_SCALE.WHITE,
      colorPrimaryHover: GRAY_SCALE.WHITE,
      colorBgContainer: baseTheme.colorPrimary,
    },
    Menu: {
      radiusItem: 0,
      activeBarWidth: 3,
      itemMarginInline: 0,
      controlHeightLG: 40,
      controlHeightSM: 22,
      itemBg: 'transparent',
      colorActiveBarHeight: 0,
      itemHoverBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemHoverColor: baseTheme.colorPrimary,
      itemSelectedColor: baseTheme.colorPrimary,
      itemSelectedBg: rgba(baseTheme.colorPrimary, 0.1),
      horizontalItemSelectedColor: baseTheme.colorPrimary,
      ...Menu,
    },
  };
};

export const lightTheme = {
  components: getBaseComponentThemeConfig(),
  token: {
    ...baseTheme,
    colorTextBase: GRAY_SCALE.GRAY,
  },
};

export const darkTheme = {
  token: {
    ...baseTheme,
    colorBgBase: DARK_MODE.BG_COLOR,
    colorText: DARK_MODE.TEXT_COLOR,
    colorTextBase: DARK_MODE.TEXT_COLOR,
    colorBorder: DARK_MODE.BORDER_BASE_COLOR,
  },
  components: getBaseComponentThemeConfig({
    Input: {
      controlOutline: 'transparent',
    },
    Calendar: {
      controlItemBgActive: '#303a4e',
    },
    Card: {
      colorTextHeading: DARK_MODE.HEADING_COLOR,
    },
    Dropdown: {
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Layout: {
      bodyBg: '#1b2531',
      headerBg: DARK_MODE.BG_COLOR,
    },
    Table: {
      colorText: DARK_MODE.TEXT_COLOR,
      colorTextHeading: DARK_MODE.HEADING_COLOR,
    },
    Button: {
      controlOutline: 'transparent',
      colorText: DARK_MODE.TEXT_COLOR,
      colorBorder: DARK_MODE.BORDER_BASE_COLOR,
    },
    Select: {
      controlOutline: 'transparent',
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    DatePicker: {
      controlOutline: 'transparent',
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
    },
    Menu: {
      activeBarWidth: 0,
      itemSelectedBg: 'transparent',
      itemHoverColor: GRAY_SCALE.WHITE,
      colorItemText: DARK_MODE.TEXT_COLOR,
      itemSelectedColor: GRAY_SCALE.WHITE,
      horizontalItemSelectedColor: GRAY_SCALE.WHITE,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
  }),
};
